import { ClipboardCheck, AlertTriangle, CheckCircle2, Info, User, Tractor, SprayCan } from 'lucide-react';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import type { PreStartMachineStatus } from '../../types/spray';

interface PreStartStatusCardProps {
  machines: PreStartMachineStatus[];
  /** Start the pre-start for one machine. Omitted for a read-only view. */
  onRunCheck?: (machine: PreStartMachineStatus) => void;
  /**
   * The spray has already been run. The card becomes a record of what was
   * checked rather than a gate — "not checked today" means nothing about a
   * spray that went out last week.
   */
  historic?: boolean;
}

const ROLE_LABEL: Record<PreStartMachineStatus['role'], string> = {
  prime_mover: 'Prime mover',
  sprayer: 'Sprayer',
};

/** "3 days ago", and not "1 days ago". */
function ageLabel(days?: number | null): string | null {
  if (days == null) return null;
  if (days <= 0) return 'earlier today';
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Whether each machine on the rig has been walked around today.
 *
 * Two machines go out on a towed spray and the card lists both, because the
 * whole point of it is that the tractor is easy to forget: it is the sprayer
 * that the job is named after, and the tractor that has the brakes, the PTO
 * guard and the hitch on it.
 *
 * Three states are deliberately distinguishable at a glance, because
 * collapsing any two of them makes the card lie:
 *
 *   checked today          - green, with who and when
 *   no checklist set up    - neutral, and says so; it passes the gate, and a
 *                            tick here would claim an inspection nobody did
 *   not checked today      - amber, with how long ago it last was
 */
function PreStartStatusCard({ machines, onRunCheck, historic = false }: PreStartStatusCardProps) {
  const timezone = useOrgTimezone();
  if (!machines.length) return null;

  const outstanding = historic ? [] : machines.filter(m => !m.satisfied);

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${
      outstanding.length ? 'border-amber-300 bg-amber-50' : 'border-neutral-200 bg-white'
    }`}>
      <div className="flex items-center gap-2 min-w-0">
        <ClipboardCheck className="h-5 w-5 text-primary-600 shrink-0" />
        <div className="min-w-0">
          <h3 className="font-medium text-sm text-neutral-800">
            {historic ? 'Pre-start checks' : 'Pre-start checks for today'}
          </h3>
          <p className="text-xs text-neutral-500">
            {historic
              ? 'What was checked on this rig'
              : 'Every machine on the rig, not just the sprayer'}
          </p>
        </div>
      </div>

      {outstanding.length > 0 && (
        <div className="flex items-start gap-1.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>
              {outstanding.length === machines.length
                ? 'A pre-start check is needed before this spray can start.'
                : `${outstanding.length === 1 ? 'One machine' : `${outstanding.length} machines`} still need a pre-start check before this spray can start.`}
            </strong>{' '}
            A check done today covers every job that machine does today.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {machines.map(m => {
          const RoleIcon = m.role === 'prime_mover' ? Tractor : SprayCan;
          const done = m.checked_today;
          const who = [m.last_completion?.completed_by_first_name,
                       m.last_completion?.completed_by_last_name].filter(Boolean).join(' ');
          return (
            <li
              key={m.equipment_id}
              className="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-2.5"
            >
              <div className="flex items-start gap-2 min-w-0">
                <RoleIcon className="h-4 w-4 text-neutral-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-800 truncate">
                    {m.equipment_name}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {ROLE_LABEL[m.role]}
                    {m.custom_type_label ? ` · ${m.custom_type_label}` : ''}
                  </p>

                  {done ? (
                    <p className="text-xs text-green-700 flex items-start gap-1 mt-1">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-px" />
                      <span>
                        Checked {m.last_completion?.completed_at
                          ? formatDateTimeInTz(m.last_completion.completed_at, timezone)
                          : 'today'}
                        {who && <> by {who}</>}
                        {/* A failed check still satisfies the gate — running a
                            machine somebody found a fault on is the operator's
                            call. It must not read as a clean one, though. */}
                        {m.last_completion?.overall_pass === false && (
                          <span className="text-amber-700 font-medium"> — items failed</span>
                        )}
                      </span>
                    </p>
                  ) : !m.has_template ? (
                    <p className="text-xs text-neutral-500 flex items-start gap-1 mt-1">
                      <Info className="h-3.5 w-3.5 shrink-0 mt-px" />
                      No pre-start checklist configured for this equipment type
                    </p>
                  ) : (
                    <p className="text-xs text-amber-800 flex items-start gap-1 mt-1">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                      {m.last_completion
                        ? <>Last checked {ageLabel(m.last_completion.age_days) ?? 'some time ago'}</>
                        : <>Never checked</>}
                    </p>
                  )}

                  {done && who && m.last_completion?.template_name && (
                    <p className="text-xs text-neutral-400 flex items-center gap-1 mt-0.5">
                      <User className="h-3 w-3 shrink-0" />
                      {m.last_completion.template_name}
                    </p>
                  )}
                </div>
              </div>

              {onRunCheck && !historic && (
                <button
                  type="button"
                  onClick={() => onRunCheck(m)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors shrink-0 ${
                    m.satisfied
                      ? 'border border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                  }`}
                >
                  {done ? 'Check again' : 'Pre-start'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default PreStartStatusCard;
